import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ProductService from '../../services/Product.service';
import type { RootState } from '../store';

export interface Product {
  id: number;
  code: string;
  price: number;
  totalPrice: number;
  checkoutSmallText: string;
  checkoutDescription: string;
  items: Array<{ code: string; item: string; factor: string; price: number }>;
  label: string;
  currency?: string;
  isDoctorPayment?: boolean;
}

type RawProductFromApi = Omit<Product, 'items'> & {
  items: string;
};

interface ProductDetailsState {
  data: Product | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ProductDetailsState = {
  data: null,
  status: 'idle',
  error: null,
};

// --- Async Thunk for fetching product details ---
export const fetchProductDetailsByCode = createAsyncThunk<
  Product,
  string,
  {
    rejectValue: string;
  }
>('productDetails/fetchByCode', async (code: string, { rejectWithValue }) => {
  try {
    const response: {
      data?: RawProductFromApi;
      error?: boolean;
      message?: string;
    } = await ProductService.getProductByCode(code);

    if (response.error || !response.data) {
      return rejectWithValue(response.message || 'Failed to fetch product data from backend.');
    }

    const rawProduct: RawProductFromApi = response.data;

    let parsedItems: Product['items'] = [];
    try {
      if (rawProduct.items && typeof rawProduct.items === 'string') {
        parsedItems = JSON.parse(rawProduct.items);
      } else {
        console.warn(`Product items for code ${code} is missing or not a string.`);
      }
    } catch (e: any) {
      console.error(`Failed to parse product items JSON for code ${code}:`, e);

      return rejectWithValue(`Product items data is malformed for code ${code}. Details: ${e.message}`);
    }

    const product: Product = {
      ...rawProduct,
      items: parsedItems,
    };

    return product;
  } catch (error: any) {
    return rejectWithValue(error.message || `Unexpected error fetching product for code ${code}`);
  }
});

export const productDetailsSlice = createSlice({
  name: 'productDetails',
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.data = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductDetailsByCode.pending, (state) => {
        state.status = 'loading';
        state.data = null;
        state.error = null;
      })
      .addCase(fetchProductDetailsByCode.fulfilled, (state, action: PayloadAction<Product>) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchProductDetailsByCode.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) ?? action.error.message ?? 'Unknown error';
      });
  },
});

export const { clearCurrentProduct } = productDetailsSlice.actions;

export const selectCurrentProduct = (state: RootState) => state.productDetails.data;
export const selectProductStatus = (state: RootState) => state.productDetails.status;
export const selectProductError = (state: RootState) => state.productDetails.error;

// eslint-disable-next-line import/no-default-export
export default productDetailsSlice.reducer;
